// Activity Registration Component
// User interface for registering for activities with countdown timer

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Clock, 
  Users, 
  MapPin, 
  Calendar,
  CheckCircle, 
  AlertCircle,
  UserPlus,
  Activity,
  Award,
  Timer,
  X
} from 'lucide-react';
import { useRealtime } from '../contexts/RealtimeContext';
import { 
  listenToActivityRegistration, 
  registerForActivity,
  listenToActivityParticipants,
  isRegistrationOpen,
  isRegistrationExpired,
  getRegistrationTimeRemaining
} from '../firebase/activityRegistrations';
import toast from 'react-hot-toast';

const ActivityRegistration = ({ registrationId }) => {
  const { currentUser } = useRealtime();
  const [registration, setRegistration] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isRegistering, setIsRegistering] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [userRegistered, setUserRegistered] = useState(false);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [registrationForm, setRegistrationForm] = useState({
    name: '',
    email: '',
    mobile: ''
  });

  // Calculate time remaining
  const calculateTimeLeft = useCallback((endTime) => {
    const now = new Date().getTime();
    const end = new Date(endTime).getTime();
    const difference = end - now;
    
    if (difference <= 0) {
      setIsExpired(true);
      return 0;
    }
    
    return difference;
  }, []);

  // Format time display
  const formatTime = (milliseconds) => {
    const days = Math.floor(milliseconds / (1000 * 60 * 60 * 24));
    const hours = Math.floor((milliseconds % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((milliseconds % (1000 * 60)) / 1000);

    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  // Load registration data
  useEffect(() => {
    if (!registrationId) return;

    const unsubscribe = listenToActivityRegistration(registrationId, (result) => {
      if (result.success) {
        setRegistration(result.data);
        setIsLoading(false);
        
        // Check registration status
        const isOpenStatus = isRegistrationOpen(result.data);
        const isExpiredStatus = isRegistrationExpired(result.data);
        setIsOpen(isOpenStatus);
        setIsExpired(isExpiredStatus);
        
        // Calculate initial time left
        if (result.data.registrationEndTime) {
          const timeRemaining = calculateTimeLeft(result.data.registrationEndTime);
          setTimeLeft(timeRemaining);
        }
      } else {
        setIsLoading(false);
      }
    });

    return unsubscribe;
  }, [registrationId, calculateTimeLeft]);

  // Load participants data
  useEffect(() => {
    if (!registrationId) return;

    const unsubscribe = listenToActivityParticipants(registrationId, (result) => {
      if (result.success) {
        setParticipants(result.data);
        
        // Check if current user has registered
        if (currentUser) {
          const userRegistration = result.data.find(participant => participant.userId === currentUser.uid);
          setUserRegistered(!!userRegistration);
        }
      }
    });

    return unsubscribe;
  }, [registrationId, currentUser]);

  // Countdown timer
  useEffect(() => {
    if (!registration?.registrationEndTime || isExpired) return;

    const timer = setInterval(() => {
      const timeRemaining = calculateTimeLeft(registration.registrationEndTime);
      setTimeLeft(timeRemaining);
      
      if (timeRemaining <= 0) {
        setIsExpired(true);
        setIsOpen(false);
        clearInterval(timer);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [registration?.registrationEndTime, calculateTimeLeft, isExpired]);

  // Handle registration form input
  const handleFormInput = (field, value) => {
    setRegistrationForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle registration
  const handleRegister = async () => {
    if (!currentUser) {
      toast.error('Please login to register');
      return;
    }

    if (!isOpen) {
      toast.error('Registration is not open');
      return;
    }

    if (userRegistered) {
      toast.error('You have already registered for this activity');
      return;
    }

    // Validate form
    if (!registrationForm.name.trim() || !registrationForm.email.trim() || !registrationForm.mobile.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(registrationForm.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Basic mobile validation
    const mobileRegex = /^[0-9]{10}$/;
    if (!mobileRegex.test(registrationForm.mobile.replace(/\D/g, ''))) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }

    setIsRegistering(true);
    try {
      const userData = {
        name: registrationForm.name.trim(),
        email: registrationForm.email.trim(),
        mobile: registrationForm.mobile.trim(),
        uid: currentUser.uid
      };

      const result = await registerForActivity(registrationId, currentUser.uid, userData);
      if (result.success) {
        toast.success('Successfully registered for the activity!');
        setUserRegistered(true);
        setShowRegistrationForm(false);
        setRegistrationForm({ name: '', email: '', mobile: '' });
      } else {
        toast.error('Failed to register: ' + result.error);
      }
    } catch (error) {
      toast.error('Error registering: ' + error.message);
    } finally {
      setIsRegistering(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading registration...</span>
      </div>
    );
  }

  if (!registration) {
    return (
      <div className="text-center p-8">
        <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          Registration not found
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          The requested activity registration could not be found.
        </p>
      </div>
    );
  }

  const fillRate = Math.round((participants.length / registration.maxParticipants) * 100);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-500 to-blue-500 p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-2 flex items-center">
              <Activity className="h-6 w-6 mr-2" />
              {registration.title}
            </h2>
            <p className="text-green-100">{registration.description}</p>
          </div>
          
          <div className="text-right">
            <div className="flex items-center text-green-100 mb-2">
              <Users className="h-4 w-4 mr-1" />
              <span className="text-sm">{participants.length}/{registration.maxParticipants} participants</span>
            </div>
            
            {!isExpired ? (
              <div className="flex items-center text-green-100">
                <Clock className="h-4 w-4 mr-1" />
                <span className="text-sm font-medium">
                  {formatTime(timeLeft)}
                </span>
              </div>
            ) : (
              <div className="flex items-center text-green-100">
                <X className="h-4 w-4 mr-1" />
                <span className="text-sm font-medium">Registration Closed</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Registration Content */}
      <div className="p-6">
        {/* Activity Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="space-y-4">
            <div className="flex items-center">
              <Calendar className="h-5 w-5 text-gray-500 mr-3" />
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Activity Date</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {formatDate(registration.activityDate)}
                </p>
              </div>
            </div>
            
            <div className="flex items-center">
              <MapPin className="h-5 w-5 text-gray-500 mr-3" />
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Location</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {registration.location}
                </p>
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center">
              <Activity className="h-5 w-5 text-gray-500 mr-3" />
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Activity Type</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {registration.activityType}
                </p>
              </div>
            </div>
            
            <div className="flex items-center">
              <Users className="h-5 w-5 text-gray-500 mr-3" />
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Capacity</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {registration.maxParticipants} participants
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Requirements */}
        {registration.requirements && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Requirements
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {registration.requirements}
            </p>
          </div>
        )}

        {/* Registration Status */}
        <div className="mb-6">
          <div className={`p-4 rounded-lg ${
            isOpen 
              ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
              : isExpired
              ? 'bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800'
              : 'bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                {isOpen ? (
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                ) : isExpired ? (
                  <AlertCircle className="h-5 w-5 text-orange-500 mr-2" />
                ) : (
                  <Clock className="h-5 w-5 text-gray-500 mr-2" />
                )}
                <span className={`font-medium ${
                  isOpen 
                    ? 'text-green-800 dark:text-green-200'
                    : isExpired
                    ? 'text-orange-800 dark:text-orange-200'
                    : 'text-gray-800 dark:text-gray-200'
                }`}>
                  {isOpen ? 'Registration Open' : isExpired ? 'Registration Closed' : 'Registration Not Started'}
                </span>
              </div>
              
              {!isExpired && (
                <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                  <Timer className="h-4 w-4 mr-1" />
                  <span>Ends: {formatDate(registration.registrationEndTime)}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Registration Progress
            </span>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {fillRate}% filled
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${fillRate}%` }}
              transition={{ duration: 1 }}
              className="bg-gradient-to-r from-green-500 to-blue-500 h-2 rounded-full"
            />
          </div>
        </div>

        {/* Registration Button */}
        {isOpen && !userRegistered && (
          <div className="text-center">
            <button
              onClick={() => setShowRegistrationForm(true)}
              disabled={participants.length >= registration.maxParticipants}
              className={`w-full py-3 px-6 rounded-lg font-medium transition-all duration-200 ${
                participants.length >= registration.maxParticipants
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 text-white shadow-lg hover:shadow-xl'
              }`}
            >
              {participants.length >= registration.maxParticipants ? (
                'Registration Full'
              ) : (
                <div className="flex items-center justify-center">
                  <UserPlus className="h-4 w-4 mr-2" />
                  Register Now
                </div>
              )}
            </button>
          </div>
        )}

        {/* User Status */}
        {userRegistered && (
          <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
            <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
            <h3 className="text-lg font-semibold text-green-800 dark:text-green-200 mb-1">
              You're Registered!
            </h3>
            <p className="text-sm text-green-600 dark:text-green-400">
              You have successfully registered for this activity.
            </p>
          </div>
        )}

        {/* Participants Count (for users) - No detailed list */}
        {participants.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center justify-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="text-center">
                <div className="flex items-center justify-center mb-2">
                  <Users className="h-5 w-5 text-gray-500 mr-2" />
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    {participants.length} participant{participants.length !== 1 ? 's' : ''} registered
                  </span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-500">
                  Participant details are only visible to administrators
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Registration Form Modal */}
      <AnimatePresence>
        {showRegistrationForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Register for Activity
                  </h2>
                  <button
                    onClick={() => {
                      setShowRegistrationForm(false);
                      setRegistrationForm({ name: '', email: '', mobile: '' });
                    }}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={registrationForm.name}
                      onChange={(e) => handleFormInput('name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={registrationForm.email}
                      onChange={(e) => handleFormInput('email', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      placeholder="Enter your email address"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Mobile Number *
                    </label>
                    <input
                      type="tel"
                      value={registrationForm.mobile}
                      onChange={(e) => handleFormInput('mobile', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      placeholder="Enter your 10-digit mobile number"
                      maxLength="10"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => {
                      setShowRegistrationForm(false);
                      setRegistrationForm({ name: '', email: '', mobile: '' });
                    }}
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleRegister}
                    disabled={isRegistering}
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isRegistering ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Registering...
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <UserPlus className="h-4 w-4 mr-2" />
                        Register
                      </div>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ActivityRegistration;
